import { useContext, useEffect, useRef, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import ApplicationContext from "../../context/application/ApplicationContext";
import AuthContext from "../../context/auth/AuthContext";
import AuthStepHeader from "../../components/Auth/AuthStepHeader";
import ApplicationSelect from "../../components/ApplicationSelect/ApplicationSelect";
import NewApplicationModal from "../../components/TenantDetails/NewApplicationModal";
import { Button, ChoiceGroup } from "../../components/design";

/**
 * First guarantor step: pick the application the guarantor is linked to, then
 * add their documents yourself, or email them a request.
 * onBack goes to the profile page, onContinue gets "provide" or "send" and
 * the application id.
 */
export default function GuarantorChoiceStep({
  onBack,
  onContinue,
  initialApplicationId = "",
}) {
  const { _t } = useTranslator();
  const { myApplications, applicationsLoaded, loadApplicationsData } =
    useContext(ApplicationContext);
  const { user } = useContext(AuthContext);
  // Applications where they are a guarantor are shown but can't be picked:
  // a guarantor cannot have guarantors there (the backend refuses them too)
  const userEmail = user?.email?.toLowerCase();
  const isGuarantorIn = (app) =>
    (app?.participants ?? []).some(
      (participant) =>
        !participant?.is_deleted &&
        participant?.is_guarantor === true &&
        participant?.email?.toLowerCase() === userEmail
    );
  const applications = myApplications ?? [];
  const [choice, setChoice] = useState("provide");
  const [applicationId, setApplicationId] = useState(initialApplicationId);
  const [showNewApplication, setShowNewApplication] = useState(false);
  // ids before "create new", so the new application gets selected
  const idsBeforeCreate = useRef(null);

  // a preselected id (from the link) only counts when it is in the list
  const selectedApplicationId = applications.some(
    (app) => String(app._id) === String(applicationId) && !isGuarantorIn(app)
  )
    ? applicationId
    : "";

  useEffect(() => {
    if (!applicationsLoaded) loadApplicationsData();
  }, []);

  useEffect(() => {
    if (!idsBeforeCreate.current) return;
    const created = (myApplications ?? []).find(
      (app) => !idsBeforeCreate.current.has(String(app._id))
    );
    if (created) {
      setApplicationId(created._id);
      idsBeforeCreate.current = null;
    }
  }, [myApplications]);

  return (
    <main className="guarantorPage">
      <div className="guarantorPage__col">
        <AuthStepHeader
          onBack={onBack}
          title={_t("wouldYouLikeToProvideGuarantorDocuments")}
          subtitle={_t("guarantorChooseSub")}
        />
        <ApplicationSelect
          className="guarantorPage__application"
          applications={applications}
          value={selectedApplicationId}
          onChange={(value) => setApplicationId(value)}
          onCreate={() => {
            idsBeforeCreate.current = new Set(
              (myApplications ?? []).map((app) => String(app._id))
            );
            setShowNewApplication(true);
          }}
          placeholder={_t("guarantorSelectApplication")}
          isDisabled={isGuarantorIn}
          disabledTooltip={_t(
            "youAreAGuarantorInApplicationSoYouCannotAddGuarantor",
            "You are a guarantor in this application, so you can't add a guarantor to it."
          )}
        />
        <ChoiceGroup
          name="guarantor-choice"
          layout="list"
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          options={[
            {
              value: "provide",
              label: _t("provideGuarantorDocments"),
              description: _t("guarantorProvideDocsDesc"),
            },
            {
              value: "send",
              label: _t("sendRequestToProvideDocuments"),
              description: _t("guarantorSendRequestDesc"),
            },
          ]}
        />
        <Button
          variant="primary"
          size="lg"
          block
          className="guarantorPage__next"
          disabled={!selectedApplicationId}
          onClick={() => onContinue(choice, selectedApplicationId)}
        >
          {_t("continue")}
        </Button>
      </div>
      <NewApplicationModal
        isOpen={showNewApplication}
        onClose={() => setShowNewApplication(false)}
        // createApplication already reloads the list
        onSuccess={() => {}}
      />
    </main>
  );
}
